import { Image, ImageProps } from '@chakra-ui/react'
import {
  Button,
  FileDrop,
  IconButton,
  InputContainer,
  InputContainerProps,
  PencilSquareIcon,
} from 'components'
import { SingleUseMediaDialog, SingleUseMediaDialogProps, VideoPlayer } from 'components/media'
import { ReactElement, useState } from 'react'
import { acceptedImages, acceptedVideos, sleep, useStorageMeta, useStorageUrl } from 'utils'

export interface Props
  extends Partial<InputContainerProps>, Pick<SingleUseMediaDialogProps, DialogMediaProps> {
  compact?: boolean
  dialogProps?: Omit<SingleUseMediaDialogProps, DialogMediaProps | 'open'>
  editIcon?: ReactElement
  editLabel?: string
  fallbackFilePath?: string
  imageProps?: ImageProps
}

type DialogMediaProps = 'defaultImage' | 'filePath' | 'noFile' | 'onClose' | 'type' | 'siteUrl'

export default function SingleUseMediaInput({
  compact,
  defaultImage,
  dialogProps,
  editIcon,
  editLabel = 'Edit',
  fallbackFilePath,
  filePath,
  siteUrl,
  imageProps,
  noFile,
  onClose,
  type,
  ...rest
}: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [newMedia, setNewMedia] = useState<File>()

  const displayFilePath = noFile ? fallbackFilePath : filePath
  const { url, refetch } = useStorageUrl(displayFilePath)
  const { meta } = useStorageMeta(noFile ? undefined : filePath)

  const typeFromMeta = meta?.contentType?.includes('image') ? 'image' : 'video'
  const fileType = type || typeFromMeta

  async function handleFiles(files: File[]) {
    setNewMedia(files[0])
    await sleep(200)
    setIsEditing(true)
  }

  return (
    <InputContainer
      height={compact ? '40px' : '200px'}
      label="Image"
      flexDirection={compact ? 'row-reverse' : 'column'}
      labelActions={
        compact ? (
          <IconButton
            aria-label="Edit"
            size="sm"
            palette="primary"
            variant="ghost"
            icon={<PencilSquareIcon type="outline" />}
            onClick={() => setIsEditing(true)}
          />
        ) : (
          <Button
            size="sm"
            palette="primary"
            variant="ghost"
            leftIcon={<PencilSquareIcon type="outline" />}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )
      }
      gap={1}
      {...rest}
    >
      <FileDrop
        onDrop={handleFiles}
        options={{
          accept: {
            ...(type !== 'image' && acceptedVideos),
            ...(type !== 'video' && acceptedImages),
          },
        }}
        display="flex"
        flexDirection="column"
        alignItems="stretch"
      >
        {() =>
          fileType === 'image' ? (
            <Image
              height={compact ? '40px' : '200px'}
              objectFit={compact ? 'contain' : 'cover'}
              cursor="pointer"
              onClick={() => setIsEditing(true)}
              src={noFile ? defaultImage : url}
              borderRadius="lg"
              {...imageProps}
            />
          ) : (
            <VideoPlayer width="100%" height="200px" src={url} />
          )
        }
      </FileDrop>

      <SingleUseMediaDialog
        open={isEditing}
        newMedia={newMedia}
        defaultImage={defaultImage}
        siteUrl={siteUrl}
        noFile={noFile}
        filePath={filePath}
        onClose={async action => {
          await onClose?.(action)
          setIsEditing(false)
          setNewMedia(undefined)
          refetch()
        }}
        type={type}
        {...dialogProps}
      />
    </InputContainer>
  )
}
